/**
 * @file "Finish setting up" — the onboarding ramp, and the ONE place a setup target
 * becomes a route.
 *
 * ## What this is, and what it is not
 *
 * The dashboard told a new landlord nothing about what to do next: every step was
 * possible (a property, its units, a lease, the charges, a payment) and the path
 * between them was invisible. This card is the path. It renders the five steps the
 * SERVER computed, in the server's order, with the next one emphasised, and it
 * disappears the moment `setup.next` is `null` — it is setup guidance, not permanent
 * furniture. A landlord who adds a second building and leaves a unit empty is caught
 * by the "Needs your attention" row below, which never goes away.
 *
 * ## Why the numbers are the server's and the routes are ours
 *
 * A step's `count` is the payload's own figure — the metric beside this card and the
 * step's own line are the same number because the server folded them from the same
 * rows in one transaction, and this component does no arithmetic that could disagree.
 * The `target` is a DESCRIPTOR (`{ kind, id? }`), never a path: the API names the
 * THING, and `TargetLink` is the only place that knows the router's shape. A route
 * rename is therefore a change in this file and nowhere in the API.
 *
 * ## Why the fifth step is not "invite your tenants"
 *
 * The tenant portal (T1.7) does not exist. A ramp is a promise about what the app can
 * do, so it contains exactly the five things it can do: the cycle from an empty
 * portfolio to money recorded. Nothing here implies a capability that is not built.
 */
import {
  Banknote,
  Building2,
  CalendarClock,
  CheckCircle2,
  ChevronRight,
  DoorOpen,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import type { SetupProgress, SetupStep, SetupStepKey, SetupTarget } from "../api/types";
import { Card } from "./ui/card";

/**
 * The five labels, keyed by the server's step keys.
 *
 * Copy, not data: the API sends the key and the count, and the words live with the
 * screen that renders them (the empty states write their own sentences for the same
 * reason). A key with no label is a compile error, so the union cannot grow without
 * this map growing with it.
 */
const STEP_LABELS: Readonly<Record<SetupStepKey, string>> = {
  property: "Add your first property",
  units: "Add its units",
  lease: "Create a lease",
  charges: "Generate the charges",
  payment: "Record a payment",
};

/** What each step is, in one line — the shape of the journey, not a second spec. */
const STEP_DETAILS: Readonly<Record<SetupStepKey, string>> = {
  property: "The building everything else hangs off.",
  units: "A lease attaches to a unit, not to a building.",
  lease: "The agreement that turns a unit into a tenancy.",
  charges: "The rent that tenancy owes, period by period.",
  payment: "Money that actually arrived, applied to a charge.",
};

/** The noun a step counts, singular and plural. */
const STEP_NOUNS: Readonly<Record<SetupStepKey, readonly [string, string]>> = {
  property: ["property", "properties"],
  units: ["unit", "units"],
  lease: ["lease", "leases"],
  charges: ["charge", "charges"],
  payment: ["payment", "payments"],
};

/**
 * A step's count, in words.
 *
 * Zero says "None yet" rather than "0 units": a rendered zero reads as a measurement
 * of the world, and this is a checklist. The number is ALWAYS the server's when it is
 * shown — nothing here counts anything.
 */
function countPhrase(step: SetupStep): string {
  if (step.count === 0) return "None yet";
  const [one, many] = STEP_NOUNS[step.key];
  return `${String(step.count)} ${step.count === 1 ? one : many}`;
}

/**
 * A link to whatever a setup target names.
 *
 * The switch is the whole point: this is the only code that knows `kind: "unit"`
 * means `/workspaces/$workspaceId/units/$unitId`. A descriptor that reaches here
 * without the `id` it needs falls back to the LIST of that noun rather than rendering
 * nothing — a real page is always a better answer than a dead link, and the server
 * uses the same fallback when it has nothing specific to name.
 */
export function TargetLink({
  workspaceId,
  target,
  current = false,
  className,
  title,
  children,
}: {
  readonly workspaceId: string;
  readonly target: SetupTarget;
  /** Marks the step the ramp is on: `aria-current="step"` on the link. */
  readonly current?: boolean;
  readonly className?: string;
  readonly title?: string;
  readonly children: ReactNode;
}) {
  // `aria-current="step"` is the semantic marker for "this is where you are in the
  // sequence", and it is what makes the emphasised step assertable without reading
  // CSS. It is also the marker a screen reader announces.
  const marker = current ? ({ "aria-current": "step" } as const) : {};

  if (target.kind === "property" && target.id !== undefined) {
    return (
      <Link
        to="/workspaces/$workspaceId/properties/$propertyId"
        params={{ workspaceId, propertyId: target.id }}
        className={className}
        title={title}
        {...marker}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === "unit") {
    // No `id` means the descriptor named a unit but not which one, which should not
    // happen — and landing on the units list is still a real page, where the unit the
    // card is about is one row away. A dead link would be the alternative.
    if (target.id === undefined) {
      return (
        <Link
          to="/workspaces/$workspaceId/units"
          params={{ workspaceId }}
          className={className}
          title={title}
          {...marker}
        >
          {children}
        </Link>
      );
    }
    return (
      <Link
        to="/workspaces/$workspaceId/units/$unitId"
        params={{ workspaceId, unitId: target.id }}
        className={className}
        title={title}
        {...marker}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === "rent-charges") {
    return (
      <Link
        to="/workspaces/$workspaceId/rent-charges"
        params={{ workspaceId }}
        className={className}
        title={title}
        {...marker}
      >
        {children}
      </Link>
    );
  }
  if (target.kind === "payments") {
    return (
      <Link
        to="/workspaces/$workspaceId/payments"
        params={{ workspaceId }}
        className={className}
        title={title}
        {...marker}
      >
        {children}
      </Link>
    );
  }
  // `properties` — the list, and the same fallback for a `property` descriptor that
  // arrived without its id.
  return (
    <Link
      to="/workspaces/$workspaceId/properties"
      params={{ workspaceId }}
      className={className}
      title={title}
      {...marker}
    >
      {children}
    </Link>
  );
}

/** The ramp. Rendered above the dashboard's sections, and absent when it is done. */
export function SetupRamp({
  workspaceId,
  setup,
}: {
  readonly workspaceId: string;
  readonly setup: SetupProgress;
}) {
  // The card IS the ramp: a complete cycle has nothing to guide, so it renders
  // nothing at all rather than a congratulation nobody asked to keep.
  if (setup.next === null) return null;

  return (
    <section aria-labelledby="setup-heading" className="flex flex-col gap-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 id="setup-heading" className="text-lg font-semibold tracking-tight">
          Finish setting up
        </h2>
        {/* The progress line is the server's own fold, not a count of this list. */}
        <p className="text-sm text-muted-foreground" role="status">
          {setup.completed} of {setup.total} done
        </p>
      </div>

      <Card className="gap-3 px-4 py-4 sm:px-6">
        <p className="text-xs text-muted-foreground">
          The cycle once, from an empty portfolio to money recorded: a property, its
          units, a lease, the charges that tenancy owes, and the payment that settles
          them.
        </p>

        <ol className="flex flex-col gap-2">
          {setup.steps.map((step) => (
            <li key={step.key} data-setup-step={step.key} data-setup-done={String(step.done)}>
              {step.done ? (
                /*
                  A done step is NOT a link. It happened; there is no action left on
                  it, and a link would offer one that does not exist. The tick and the
                  count are what it says instead.
                */
                <div className="flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3">
                  <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden="true" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-muted-foreground">
                      {STEP_LABELS[step.key]}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {countPhrase(step)}
                    </span>
                  </span>
                  <span className="shrink-0 text-xs font-medium text-primary">Done</span>
                </div>
              ) : (
                <TargetLink
                  workspaceId={workspaceId}
                  target={step.target}
                  current={step.key === setup.next}
                  className={
                    step.key === setup.next
                      ? "flex items-center gap-3 rounded-lg border border-primary bg-primary/5 px-4 py-3 ring-1 ring-primary/30 hover:border-primary"
                      : "flex items-center gap-3 rounded-lg border border-border bg-card px-4 py-3 hover:border-primary"
                  }
                >
                  <RowIcon step={step.key} emphasised={step.key === setup.next} />
                  <span className="min-w-0 flex-1">
                    <span
                      className={
                        step.key === setup.next
                          ? "block text-sm font-semibold text-foreground"
                          : "block text-sm font-medium text-foreground"
                      }
                    >
                      {STEP_LABELS[step.key]}
                    </span>
                    <span className="block text-xs text-muted-foreground">
                      {countPhrase(step)} · {STEP_DETAILS[step.key]}
                    </span>
                  </span>
                  {step.key === setup.next ? (
                    <span className="shrink-0 rounded-full bg-primary px-2 py-0.5 text-xs font-medium text-primary-foreground">
                      Next
                    </span>
                  ) : null}
                  <ChevronRight
                    className="size-4 shrink-0 text-muted-foreground"
                    aria-hidden="true"
                  />
                </TargetLink>
              )}
            </li>
          ))}
        </ol>
      </Card>
    </section>
  );
}

/** One icon per step, so the journey reads at a glance. */
function RowIcon({
  step,
  emphasised,
}: {
  readonly step: SetupStepKey;
  readonly emphasised: boolean;
}) {
  const icons: Readonly<Record<SetupStepKey, LucideIcon>> = {
    property: Building2,
    units: DoorOpen,
    lease: CalendarClock,
    charges: Banknote,
    payment: CheckCircle2,
  };
  const Icon = icons[step];
  return (
    <Icon
      className={
        emphasised ? "size-4 shrink-0 text-primary" : "size-4 shrink-0 text-muted-foreground"
      }
      aria-hidden="true"
    />
  );
}
